import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Building2, Send, CheckCircle } from "lucide-react";

const COUNTRIES = [
  { value: "DO", label: "República Dominicana" },
  { value: "PR", label: "Puerto Rico" },
  { value: "MX", label: "México" },
  { value: "CO", label: "Colombia" },
  { value: "AR", label: "Argentina" },
  { value: "CL", label: "Chile" },
  { value: "PE", label: "Perú" },
  { value: "VE", label: "Venezuela" },
  { value: "EC", label: "Ecuador" },
  { value: "PA", label: "Panamá" },
  { value: "CR", label: "Costa Rica" },
  { value: "GT", label: "Guatemala" },
  { value: "CU", label: "Cuba" },
  { value: "UY", label: "Uruguay" },
  { value: "PY", label: "Paraguay" },
  { value: "BO", label: "Bolivia" },
  { value: "HN", label: "Honduras" },
  { value: "SV", label: "El Salvador" },
  { value: "NI", label: "Nicaragua" },
  { value: "BR", label: "Brasil" },
  { value: "US", label: "Estados Unidos" },
  { value: "ES", label: "España" },
  { value: "OTHER", label: "Otro" },
];

const clubAccessSchema = z.object({
  clubName: z.string().min(2, "El nombre del club es requerido"),
  contactName: z.string().min(2, "Tu nombre es requerido"),
  email: z.string().email("Email inválido"),
  phone: z.string().optional(),
  courtType: z.enum(["indoor", "outdoor", "both"]).optional(),
  numberOfCourts: z.number().min(1).optional(),
  country: z.string().optional(),
  city: z.string().optional(),
  clubStatus: z.enum(["operating", "opening_soon", "opening_later"]).optional(),
  message: z.string().optional(),
});

type ClubAccessFormData = z.infer<typeof clubAccessSchema>;

interface ClubAccessRequestModalProps {
  trigger?: React.ReactNode;
}

export function ClubAccessRequestModal({ trigger }: ClubAccessRequestModalProps) {
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();

  const form = useForm<ClubAccessFormData>({
    resolver: zodResolver(clubAccessSchema),
    defaultValues: {
      clubName: "",
      contactName: "",
      email: "",
      phone: "",
      courtType: undefined,
      numberOfCourts: undefined,
      country: "",
      city: "",
      clubStatus: undefined,
      message: "",
    },
  });

  const submitMutation = useMutation({
    mutationFn: async (data: ClubAccessFormData) => {
      return apiRequest("POST", "/api/club-access-request", data);
    },
    onSuccess: () => {
      setSubmitted(true);
      toast({
        title: "Solicitud enviada",
        description: "Nos pondremos en contacto contigo pronto.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "No se pudo enviar la solicitud. Intenta de nuevo.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: ClubAccessFormData) => {
    submitMutation.mutate(data);
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setTimeout(() => {
        setSubmitted(false);
        form.reset();
      }, 300);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="lg" className="w-full gap-2" data-testid="button-club-access">
            <Building2 className="h-4 w-4" />
            Solicitar Acceso para Club
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh]">
        {submitted ? (
          <div className="flex flex-col items-center py-8 text-center">
            <div className="p-4 rounded-full bg-primary/10 mb-4">
              <CheckCircle className="h-12 w-12 text-primary" />
            </div>
            <DialogTitle className="text-xl mb-2">¡Solicitud Enviada!</DialogTitle>
            <DialogDescription className="text-base">
              Hemos recibido tu solicitud. Nuestro equipo revisará la información y te contactará pronto para completar el proceso de registro.
            </DialogDescription>
            <Button 
              className="mt-6" 
              onClick={() => handleOpenChange(false)}
              data-testid="button-close-modal"
            >
              Cerrar
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                Registra tu Club
              </DialogTitle>
              <DialogDescription>
                Completa el formulario y nuestro equipo se pondrá en contacto contigo para completar el registro de tu club en CourtVibe.
              </DialogDescription>
            </DialogHeader>
            <ScrollArea className="max-h-[60vh] pr-4">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="clubName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nombre del Club *</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Club Pádel Santo Domingo" 
                            {...field} 
                            data-testid="input-club-name"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="contactName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tu Nombre *</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Juan García" 
                            {...field} 
                            data-testid="input-contact-name"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email de Contacto *</FormLabel>
                        <FormControl>
                          <Input 
                            type="email" 
                            placeholder="contacto@tuclub.com" 
                            {...field}
                            data-testid="input-email"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Teléfono</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="+1 809 000 0000" 
                            {...field}
                            data-testid="input-phone"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="courtType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Tipo de Canchas</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger data-testid="select-court-type">
                                <SelectValue placeholder="Seleccionar" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="indoor">Indoor</SelectItem>
                              <SelectItem value="outdoor">Outdoor</SelectItem>
                              <SelectItem value="both">Ambas</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="numberOfCourts"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Cantidad de Canchas</FormLabel>
                          <FormControl>
                            <Input 
                              type="number"
                              min="1"
                              placeholder="4"
                              {...field}
                              value={field.value ?? ""}
                              onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value, 10) : undefined)}
                              data-testid="input-number-of-courts"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="country"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>País</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger data-testid="select-country">
                                <SelectValue placeholder="Seleccionar" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <ScrollArea className="h-60">
                                {COUNTRIES.map((country) => (
                                  <SelectItem key={country.value} value={country.value}>
                                    {country.label}
                                  </SelectItem>
                                ))}
                              </ScrollArea>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="city"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Ciudad</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="Santo Domingo" 
                              {...field}
                              data-testid="input-city"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="clubStatus"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Estado del Club</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-club-status">
                              <SelectValue placeholder="Seleccionar" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="operating">Ya operando</SelectItem>
                            <SelectItem value="opening_soon">Abrirá en menos de 3 meses</SelectItem>
                            <SelectItem value="opening_later">Abrirá en más de 3 meses</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Mensaje (opcional)</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="Cuéntanos sobre tu club..." 
                            className="resize-none"
                            {...field}
                            data-testid="input-message"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button 
                    type="submit" 
                    className="w-full gap-2" 
                    disabled={submitMutation.isPending}
                    data-testid="button-submit-request"
                  >
                    {submitMutation.isPending ? (
                      "Enviando..."
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        Enviar Solicitud
                      </>
                    )}
                  </Button>
                </form>
              </Form>
            </ScrollArea>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
